/**
 * Lo sfodero: l'animazione del logo con cui si entra nell'app dal telefono.
 *
 * Lorenzo la vuole a OGNI ingresso: quando apre l'app dalla home e quando esce
 * dal login. Per questo il markup e il CSS non stanno in React ma in
 * index.html (un <template id="katana-tpl"> e una copia gia' istanziata,
 * #katana): al primo dipinto React non c'e' ancora, e l'animazione deve
 * partire sull'inchiostro che il telefono mostra aprendo l'app, senza nessun
 * lampo dell'interfaccia chiara in mezzo. Qui c'e' solo la meccanica: chi la
 * avvia, chi la toglie, e quando.
 *
 * IL PATTO CON IL CSS (index.html), tre nomi:
 *  - la radice ha classe `katana`;
 *  - l'animazione del fodero, `katana-sfodera`, e' quella che si ferma sul logo
 *    fermo (dura quanto tutta la sequenza, 1500 ms, e il suo animationend
 *    risale fino alla radice): quando finisce, a schermo c'e' il logo vero;
 *  - la classe `katana--via` fa partire l'uscita, animazione `katanaVia`
 *    (il foglio d'inchiostro svanisce in 180 ms, il logo non si muove);
 *    quando finisce, il nodo si toglie.
 *
 * Perche' l'uscita la comanda il JS e non il CSS: se il bundle arriva tardi
 * (rete lenta) l'animazione e' gia' finita da un pezzo e il logo fermo resta
 * li' a fare da schermata di caricamento, che e' esattamente quello che deve
 * fare. Appena React c'e', si esce. Con la sessione gia' salvata l'app dipinge
 * in 80 ms, quindi di fatto e' sempre l'animazione a decidere la durata.
 *
 * Non riparte quando si torna all'app dal cambio app: la schermata di lancio
 * c'e' solo al lancio, come nelle app native. Lorenzo lo sa.
 */
const TEMPLATE_ID = 'katana-tpl';
const RADICE = 'katana';
const FERMO = 'katana-sfodera';
const VIA = 'katanaVia';
/**
 * Se qualcosa non torna (animazione interrotta, scheda in secondo piano), il
 * nodo se ne va comunque. Deve stare largo rispetto al caso peggiore vero:
 * 1,2 s di attesa del via (index.html) + 2,25 s di sfoderata + 0,22 di
 * dissolvenza fanno 3,67 s. Deve scattare solo quando nient'altro ha
 * funzionato, mai al posto della fine naturale.
 */
const FALLBACK_MS = 4800;

/** Solo telefono, solo senza "riduci movimento", solo se index.html porta il template. */
export function katanaPossibile(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (!window.matchMedia('(max-width: 768px)').matches) return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return !!document.getElementById(TEMPLATE_ID);
}

/**
 * Prende in carico un nodo gia' in pagina: aspetta la fine del disegno, avvia
 * l'uscita, toglie il nodo. Idempotente: un nodo gia' adottato non viene
 * adottato due volte.
 */
function adotta(nodo: HTMLElement): void {
  if (nodo.dataset.adottata === '1') return;
  nodo.dataset.adottata = '1';

  let tolto = false;
  const togli = () => {
    if (tolto) return;
    tolto = true;
    nodo.remove();
    // Via anche il theme-color d'inchiostro: da qui in poi la barra di stato
    // torna a prendere il colore del tema, che lo riscrive AppShell.
    document.querySelectorAll('meta[name="theme-color"][data-katana]').forEach((m) => m.remove());
  };
  const esci = () => {
    if (tolto || nodo.classList.contains(`${RADICE}--via`)) return;
    nodo.classList.add(`${RADICE}--via`);
  };

  nodo.addEventListener('animationend', (e) => {
    const nome = (e as AnimationEvent).animationName;
    if (nome === FERMO) esci();
    else if (nome === VIA) togli();
  });

  // Il disegno e' gia' finito prima che arrivassimo (bundle lento): si esce ora.
  // "Finito" vuol dire finito, non "non in corsa": un'animazione in pausa (gli
  // strumenti di sviluppo, un banco di prova) non e' finita, e trattarla come
  // tale toglieva il foglio a meta' sfoderata. Nessuna animazione (movimento
  // ridotto, CSS non applicato) conta come finita: non c'e' niente da aspettare.
  const animazioni = nodo.getAnimations({ subtree: true });
  if (animazioni.every((a) => a.playState === 'finished')) esci();

  setTimeout(togli, FALLBACK_MS);
}

/** All'avvio: la copia gia' istanziata in index.html, se c'e', si adotta. Da chiamare prima del render. */
export function adottaKatanaIniziale(): void {
  if (typeof document === 'undefined') return;
  const nodo = document.getElementById(RADICE);
  if (!nodo) return;
  // Sul computer, o con "riduci movimento", la copia iniziale non serve: via subito.
  if (!katanaPossibile()) { nodo.remove(); return; }
  adotta(nodo);
}

/** Dopo il login: una copia nuova dal template, che riparte da zero. */
export function avviaKatana(): void {
  if (!katanaPossibile()) return;
  const tpl = document.getElementById(TEMPLATE_ID) as HTMLTemplateElement | null;
  const modello = tpl?.content.firstElementChild;
  if (!modello) return;
  document.querySelectorAll(`.${RADICE}`).forEach((e) => e.remove());
  const nodo = modello.cloneNode(true) as HTMLElement;
  nodo.id = RADICE;
  document.body.appendChild(nodo);

  // Anche qui la barra di stato prende l'inchiostro del foglio: dal login si
  // arriva a un'interfaccia chiara, e senza questo in cima resterebbe la solita
  // banda slavata. `data-katana` la fa saltare ad AppShell; la toglie `togli()`.
  if (!document.querySelector('meta[name="theme-color"][data-katana]')) {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    meta.setAttribute('content', '#1a1a1e');
    meta.setAttribute('data-katana', '');
    document.head.insertBefore(meta, document.head.firstChild);
  }

  // Qui lo schermo e' gia' davanti agli occhi (si e' appena premuto Accedi):
  // niente attese, solo il tempo di un fotogramma perche' il nodo sia
  // davvero disegnato prima che l'animazione cominci a contare.
  requestAnimationFrame(() => requestAnimationFrame(() => nodo.classList.add(`${RADICE}--vai`)));
  adotta(nodo);
}
